import { formatDateTimeEuropeBerlin } from "@/utils/europeBerlinTime";

/** The subset of a deal needed to describe its activation status. */
export type TDealStatusTimestamps = {
    isActive: boolean;
    activatedAt?: string | null;
    deactivatedAt?: string | null;
    createdAt?: string | null;
};

/**
 * The instant the deal entered its *current* status, or null when unresolvable.
 *
 * Only the timestamp matching `isActive` is read: `Deal.deactivate()` leaves
 * `activated_at` populated, so an inactive deal still carries the instant of its
 * last activation and reading the wrong field would show a stale time.
 *
 * Falls back to `createdAt` because deals are created active without stamping
 * `activated_at` — a deal that has never been toggled has entered its status at
 * creation, and that is the honest answer.
 */
export function resolveDealStatusAsOf(deal: TDealStatusTimestamps): string | null {
    const matching = deal.isActive ? deal.activatedAt : deal.deactivatedAt;
    return matching ?? deal.createdAt ?? null;
}

/**
 * `Active (as of 17/08/2026, 14:32)` / `Inactive (as of …)`, or the bare
 * `Active`/`Inactive` when no timestamp resolves or the one that does is
 * unparseable — never `Active (as of )`.
 *
 * Times are shown in Europe/Berlin like every other timestamp in the app.
 * `locale` exists so tests can pin the formatting; production leaves it unset and
 * gets the viewer's locale.
 */
export function formatDealStatusLabel(deal: TDealStatusTimestamps, locale?: string): string {
    const label = deal.isActive ? "Active" : "Inactive";
    const asOf = resolveDealStatusAsOf(deal);
    if (!asOf) return label;
    const formatted = formatDateTimeEuropeBerlin(asOf, locale);
    return formatted ? `${label} (as of ${formatted})` : label;
}
